import React from 'react'
import { BrowserRouter as Router, Route, Switch, Redirect } from 'react-router-dom'
import {
  tendering as tenderingRoutes,
  auth as authRoutes,
  organization as organizationRoutes,
  bidders as biddersRoutes,
  general as generalRoutes,
  project as projectRoutes,
  documents as documentsRoutes,
  meetings as meetingsRoutes,
  submittals as submittalsRoutes,
  schedule as scheduleRoutes,
  costControl as costControlRoutes,
  payment as paymentRoutes,
  account as accountRoutes,
  shared as sharedRoutes,
} from './index'

import DashboardLayout from '../layouts/Dashboard'
import AuthLayout from '../layouts/Auth'
import PortalLayout from '../layouts/Portal'
import PublicLayout from '../layouts/Public'
import Page404 from '../pages/auth/Page404'
import ModuleGuard from './ModuleGuard'

import { useUser } from '../hooks'
import ScrollToTop from '../components/ScrollToTop'
import { navRightsOf } from '../lib/navRights'

const childRoutes = (Layout, routes, role, user) => {
  return routes
    .filter((r) => !r.allowedRoles || !role || r.allowedRoles.includes(role))
    .filter((r) => !r.allowedEmails || !user?.email || r.allowedEmails.includes(user.email))
    .map(({ path, component: Component, children, module, moduleAction }, index) => [
      <Route
        key={index}
        path={path}
        exact
        render={(props) => (
          <Layout>
            <ModuleGuard
              module={module}
              action={moduleAction}
              projectId={props.match.params.projectId}
            >
              <Component {...props} />
            </ModuleGuard>
          </Layout>
        )}
      />,
      ...(children
        ? children
            .filter((r) => !r.allowedRoles || !role || r.allowedRoles.includes(role))
            .filter((r) => !r.allowedEmails || !user?.email || r.allowedEmails.includes(user.email))
            .map(({ path, component: Component, module, moduleAction, guardPolicy }, childIndex) => (
              <Route
                key={`${index}-${childIndex}`}
                path={path}
                exact
                render={(props) => (
                  <Layout>
                    <ModuleGuard
                      module={module}
                      action={moduleAction}
                      projectId={props.match.params.projectId}
                      guardPolicy={guardPolicy}
                    >
                      <Component {...props} />
                    </ModuleGuard>
                  </Layout>
                )}
              />
            ))
        : []),
    ])
}

const Routes = () => {
  const user = useUser()

  const AnonymousRoutes = () => (
    <Switch>
      {childRoutes(PublicLayout, sharedRoutes, null, null)}
      {childRoutes(AuthLayout, authRoutes, null, null)}
      <Redirect to="/auth/signin" />
      <Route
        render={() => (
          <AuthLayout>
            <Page404 />
          </AuthLayout>
        )}
      />
    </Switch>
  )

  const UserRoutes = ({ role, user }) => {
    return (
      <Switch>
        {childRoutes(PublicLayout, sharedRoutes, role, user)}
        {childRoutes(DashboardLayout, generalRoutes, role, user)}
        {childRoutes(DashboardLayout, tenderingRoutes, role, user)}
        {childRoutes(DashboardLayout, documentsRoutes, role, user)}
        {childRoutes(DashboardLayout, meetingsRoutes, role, user)}
        {childRoutes(DashboardLayout, submittalsRoutes, role, user)}
        {childRoutes(DashboardLayout, scheduleRoutes, role, user)}
        {childRoutes(DashboardLayout, costControlRoutes, role, user)}
        {childRoutes(DashboardLayout, projectRoutes, role, user)}
        {childRoutes(DashboardLayout, organizationRoutes, role, user)}
        {childRoutes(AuthLayout, authRoutes, role, user)}
        {childRoutes(DashboardLayout, paymentRoutes, role, user)}
        {childRoutes(DashboardLayout, accountRoutes, role, user)}
        <Redirect exact from="/" to={'/dashboard'} />

        <Route
          render={() => (
            <AuthLayout>
              <Page404 />
            </AuthLayout>
          )}
        />
      </Switch>
    )
  }

  const BidderRoutes = ({ user }) => (
    <Switch>
      {childRoutes(PublicLayout, sharedRoutes, null, user)}
      {childRoutes(PortalLayout, biddersRoutes, null, user)}
      {childRoutes(AuthLayout, authRoutes, null, user)}

      <Redirect from="/" to="/auth/signin" />

      <Route
        render={() => (
          <AuthLayout>
            <Page404 />
          </AuthLayout>
        )}
      />
    </Switch>
  )

  return (
    <Router>
      <ScrollToTop>
        {!user ? (
          <AnonymousRoutes />
        ) : user.type === 'bidder' ? (
          <BidderRoutes user={user} />
        ) : (
          <UserRoutes role={navRightsOf(user.rights)} user={user} />
        )}
      </ScrollToTop>
    </Router>
  )
}

export default Routes
